import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { FilterIcon, FilterTemplateDropdown, useFilterTemplate } from '../common/FilteringTemplate';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import useColumnOrder from '../../hooks/useColumnOrder';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../utils/columnLayout';
import { buildWbsOutline, filterCollapsedRows, collectWbsPaths, computeWbsGroupSpans } from '../../utils/scheduleWbsOutline';
import { updRespMatchesViewer, scheduleRowInLookAhead, lookAheadWindow } from '../../utils/scheduleInstanceFilters';

const WBS_INDENT = 16;
const WBS_RAIL_WIDTH = 4;
const WBS_RAIL_STEP = 6;
const WBS_RAIL_LEFT = 3;
const WBS_SUMMARY_CLIP = 'polygon(6px 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 6px 100%, 0 50%)';
// WBS level colour belongs to the table bands and rails; the Gantt keeps a
// single neutral summary bar so the chart is not competing with them.
const WBS_SUMMARY_BAR = '#475569';
const WBS_LEVEL_COLOR_KEY = 'gantt-wbs-level-colors';
const DEFAULT_WBS_LEVEL_COLORS = ['#1e3a5f', '#0f766e', '#0369a1', '#7c3aed', '#c2410c', '#be185d', '#4338ca', '#15803d'];

function defaultWbsLevelColorMap() {
  return Object.fromEntries(DEFAULT_WBS_LEVEL_COLORS.map((c, i) => [String(i), c]));
}

function loadWbsLevelColors() {
  try {
    const raw = JSON.parse(localStorage.getItem(WBS_LEVEL_COLOR_KEY) || '{}');
    return { ...defaultWbsLevelColorMap(), ...raw };
  } catch {
    return defaultWbsLevelColorMap();
  }
}

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return { r: 200, g: 210, b: 220 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function mixHex(hex, toward = '#ffffff', amount = 0.82) {
  const a = hexToRgb(hex);
  const b = hexToRgb(toward);
  const m = (x, y) => Math.round(x + (y - x) * amount);
  return `rgb(${m(a.r, b.r)}, ${m(a.g, b.g)}, ${m(a.b, b.b)})`;
}

function wbsBandOf(task) {
  if (task?._kind === 'wbs') return Math.max(0, Number(task._level) || 0);
  const lvl = Number(task?._level);
  if (!Number.isFinite(lvl)) return 0;
  return Math.max(0, lvl - 1);
}

// WCAG relative luminance, so a level swatch the user picked in the legend still
// gets legible row text whether they chose navy or pale yellow.
function readableTextOn(hex) {
  const { r, g, b } = hexToRgb(hex);
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? '#0f172a' : '#ffffff';
}

/**
 * P6-style nesting rails: one vertical bar per WBS branch, in that level's
 * colour, running from the branch header row down past its last visible child.
 * Left panel only — the Gantt stays uncoloured.
 */
function WbsGroupRails({ spans, rowHeight, getColor }) {
  if (!spans?.length) return null;
  return spans.map((span) => (
    <div
      key={span.path}
      aria-hidden
      data-wbs-rail={span.level}
      style={{
        position: 'absolute',
        left: WBS_RAIL_LEFT + span.level * WBS_RAIL_STEP,
        top: span.start * rowHeight,
        height: (span.end - span.start + 1) * rowHeight,
        width: WBS_RAIL_WIDTH,
        backgroundColor: getColor(span.level),
        pointerEvents: 'none',
        zIndex: 2,
      }}
    />
  ));
}

const PALETTE = {
  piping: '#8b5cf6', mechanical: '#3b82f6', structural: '#10b981',
  hvac: '#f59e0b', electrical: '#6366f1', civil: '#14b8a6',
};
const EXTRA_COLORS = ['#ef4444', '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#78716c', '#0ea5e9', '#d946ef', '#facc15'];

const COLOR_BY_FIELDS = [
  { key: 'wbs_level', label: 'WBS Level' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'sub_discipline', label: 'Sub-Discipline' },
  { key: 'phase', label: 'Phase' },
  { key: 'resource_discipline', label: 'Resource Disc.' },
  { key: 'project', label: 'Project' },
  { key: 'shop', label: 'Shop' },
  { key: 'dr_in', label: 'Dr/In' },
  { key: 'critical', label: 'Critical' },
];

const ZOOM_LEVELS = [
  { label: 'Year',       columnWidth: 120, unit: 'year' },
  { label: 'Quarter',    columnWidth: 100, unit: 'quarter' },
  { label: 'Month',      columnWidth: 60,  unit: 'month' },
  { label: 'Month+',     columnWidth: 80,  unit: 'month' },
  { label: 'Compact',    columnWidth: 30,  unit: 'week' },
  { label: 'Normal',     columnWidth: 50,  unit: 'week' },
  { label: 'Wide',       columnWidth: 70,  unit: 'week' },
  { label: 'Expanded',   columnWidth: 90,  unit: 'week' },
  { label: 'Detailed',   columnWidth: 120, unit: 'week' },
  { label: 'Max Detail', columnWidth: 160, unit: 'week' },
];
const DEFAULT_ZOOM = 5;
const DEFAULT_LEFT_RATIO = 2 / 3;
const GANTT_PREFS = {
  order: 'gantt-column-order-v2',
  visible: 'gantt-visible-columns-v2',
  leftRatio: 'gantt-left-ratio-v2',
  zoom: 'gantt-zoom-v2',
};
const ROW_SIZE_KEY = 'gantt-row-size';
const ROW_SIZES = {
  small: { label: 'Small', height: 24, font: '0.68rem', barFont: '0.58rem', barPad: 3 },
  medium: { label: 'Medium', height: 32, font: '0.75rem', barFont: '0.65rem', barPad: 5 },
  large: { label: 'Large', height: 44, font: '0.85rem', barFont: '0.75rem', barPad: 6 },
};
const HEADER_HEIGHT = 72;

function loadPrefJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function countTimeUnits(start, end, zoomIdx) {
  if (!start || !end) return 0;
  const zl = ZOOM_LEVELS[zoomIdx];
  const c = new Date(start);
  let n = 0;
  if (zl.unit === 'week') {
    c.setDate(c.getDate() - c.getDay());
    while (c <= end && n < 800) { n += 1; c.setDate(c.getDate() + 7); }
  } else if (zl.unit === 'month') {
    c.setDate(1);
    while (c <= end && n < 400) { n += 1; c.setMonth(c.getMonth() + 1); }
  } else if (zl.unit === 'quarter') {
    c.setDate(1);
    c.setMonth(Math.floor(c.getMonth() / 3) * 3);
    while (c <= end && n < 200) { n += 1; c.setMonth(c.getMonth() + 3); }
  } else {
    c.setMonth(0);
    c.setDate(1);
    while (c <= end && n < 80) { n += 1; c.setFullYear(c.getFullYear() + 1); }
  }
  return n;
}

function fitZoomLevel(start, end, availableWidth) {
  if (!start || !end || !availableWidth) return 0;
  let best = 0;
  for (let i = 0; i < ZOOM_LEVELS.length; i += 1) {
    const width = countTimeUnits(start, end, i) * ZOOM_LEVELS[i].columnWidth;
    if (width <= availableWidth) best = i;
    else break;
  }
  return best;
}

function loadLeftRatio() {
  const n = parseFloat(localStorage.getItem(GANTT_PREFS.leftRatio));
  return Number.isFinite(n) ? Math.min(0.82, Math.max(0.28, n)) : DEFAULT_LEFT_RATIO;
}

function loadSavedZoom() {
  const n = parseInt(localStorage.getItem(GANTT_PREFS.zoom), 10);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(ZOOM_LEVELS.length - 1, n));
}
const EDITABLE_KEYS = new Set(['percent_complete', 'remaining_duration', 'notes', 'comments']);
const CREME_BG = '#FFFDE7';

const DATA_COLUMNS = [
  { key: 'notes_icon', label: '', width: 28, filterable: false, sortable: false },
  { key: 'wbs_name', label: 'WBS Name', width: 180, filterable: true, sortable: true, type: 'text', defaultHidden: true },
  { key: 'activity_id', label: 'Activity ID', width: 100, filterable: true, sortable: true, type: 'text' },
  { key: 'curve', label: 'Curve', width: 60, filterable: true, sortable: true, type: 'text' },
  { key: 'activity_name', label: 'Activity Name', width: 180, filterable: true, sortable: true, type: 'text' },
  { key: 'start_date', label: 'Start', width: 85, isDate: true, filterable: true, sortable: true, type: 'date' },
  { key: 'finish_date', label: 'Finish', width: 85, isDate: true, filterable: true, sortable: true, type: 'date' },
  { key: 'resp_party', label: 'Resp Party', width: 120, filterable: true, sortable: true, type: 'text' },
  { key: 'upd_resp', label: 'Upd Resp', width: 80, filterable: true, sortable: true, type: 'text', defaultHidden: true },
  { key: 'resource_id_name', label: 'Resource Name', width: 120, filterable: true, sortable: true, type: 'text' },
  { key: 'resource_id', label: 'Resource ID', width: 80, filterable: true, sortable: true, type: 'text' },
  { key: 'discipline', label: 'Discipline', width: 80, filterable: true, sortable: true, type: 'text' },
  { key: 'sub_discipline', label: 'SubDiscipline', width: 90, filterable: true, sortable: true, type: 'text' },
  { key: 'phase', label: 'Phase', width: 60, filterable: true, sortable: true, type: 'text' },
  { key: 'critical', label: 'Critical', width: 65, filterable: true, sortable: true, type: 'text' },
  { key: 'dr_in', label: 'Dr/In', width: 50, filterable: true, sortable: true, type: 'text' },
  { key: 'shop', label: 'Shop', width: 60, filterable: true, sortable: true, type: 'text' },
  { key: 'material_cs_ss', label: 'Material', width: 70, filterable: true, sortable: true, type: 'text' },
  { key: 'position_num', label: 'Position #', width: 70, filterable: true, sortable: true, type: 'text' },
  { key: 'project_active_forecasted', label: 'Active/Forecasted', width: 100, filterable: true, sortable: true, type: 'text' },
  { key: 'project', label: 'Project', width: 120, filterable: true, sortable: true, type: 'text' },
  { key: 'pipe_size', label: 'Pipe Size', width: 70, filterable: true, sortable: true, type: 'text' },
  { key: 'pipe_schedule', label: 'Pipe Schedule', width: 90, filterable: true, sortable: true, type: 'text' },
  { key: 'resource_discipline', label: 'Resource Disc.', width: 90, filterable: true, sortable: true, type: 'text' },
  { key: 'spreadsheet_field', label: 'Spreadsheet Field', width: 100, filterable: true, sortable: true, type: 'text' },
  { key: 'comments', label: 'Comments', width: 140, filterable: true, sortable: true, type: 'text' },
  { key: 'activity_status', label: 'Activity Status', width: 100, filterable: true, sortable: true, type: 'text', defaultHidden: true }
];

const AUTOFIT_FALLBACK_WIDTHS = {
  notes_icon: 28, wbs_name: 180, activity_id: 120, curve: 70, activity_name: 220, start_date: 100, finish_date: 100,
  resp_party: 130, resource_id_name: 150, resource_id: 100, discipline: 100, sub_discipline: 110, phase: 70, critical: 70,
  dr_in: 55, shop: 70, material_cs_ss: 85, position_num: 85, project_active_forecasted: 120,
  project: 140, pipe_size: 85, pipe_schedule: 105, resource_discipline: 110, spreadsheet_field: 120,
  duration: 80, remaining_duration: 100, percent_complete: 60, comments: 160, activity_status: 110
};

const measureTextWidth = (() => {
  let canvas;
  return (text, font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif') => {
    if (!canvas) canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = font;
    return ctx.measureText(String(text ?? '')).width;
  };
})();

const BTN = { padding: '6px 12px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px', transition: 'all 0.15s ease' };
const BTN_OFF = { ...BTN, backgroundColor: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed', border: '1px solid #e5e7eb' };

const ALL_DATA_KEYS = [
  'activity_id', 'curve', 'activity_name', 'start_date', 'finish_date',
  'resp_party', 'upd_resp', 'resource_id_name', 'resource_id', 'discipline', 'sub_discipline',
  'phase', 'dr_in', 'shop', 'material_cs_ss', 'position_num',
  'project_active_forecasted', 'project', 'pipe_size', 'pipe_schedule',
  'resource_discipline', 'spreadsheet_field', 'wbs_name', 'wbs_path', 'critical',
  'duration', 'remaining_duration', 'percent_complete', 'comments', 'activity_status'
];

const GanttChart = ({ jobNumber, data = [], onUpdateActivityNote, onUpdateActivityProgress, loading: externalLoading = false, currentUserName, lastUpdated, layout = 'activity', activityLayoutColumns = [], projectId, readOnly = false, viewerEmployeeCode = null, viewerOrgTags = [], dataDate = null, fileMenu = null }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('gantt', projectId || 'global', true);
  const containerRef = useRef(null);
  const moreBtnRef = useRef(null);
  const [moreBox, setMoreBox] = useState(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const timescaleRef = useRef(null);
  const initialAutofitDone = useRef(false);

  const [ganttData, setGanttData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(() => loadSavedZoom() ?? 0);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [leftRatio, setLeftRatio] = useState(loadLeftRatio);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => Math.round((typeof window !== 'undefined' ? window.innerWidth : 1200) * loadLeftRatio()));
  const [isDragging, setIsDragging] = useState(false);
  const zoomUserSetRef = useRef(loadSavedZoom() != null);
  const leftPanelWidthRef = useRef(leftPanelWidth);
  const zoomLevelRef = useRef(zoomLevel);
  const [activeNotePopover, setActiveNotePopover] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteHistory, setNoteHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [lookAheadOn, setLookAheadOn] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [changes, setChanges] = useState([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changeSearch, setChangeSearch] = useState('');
  const scopeJobRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [visibleDataColumns, setVisibleDataColumns] = useState({});
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [chartHeight, setChartHeight] = useState(640);
  const [rowSize, setRowSize] = useState(() => {
    try {
      const v = localStorage.getItem(ROW_SIZE_KEY);
      return ROW_SIZES[v] ? v : 'medium';
    } catch {
      return 'medium';
    }
  });
  const rowSpec = ROW_SIZES[rowSize] || ROW_SIZES.medium;
  const rowHeight = rowSpec.height;
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [barLabelMode, setBarLabelMode] = useState('inside');
  const [colorByField, setColorByField] = useState('wbs_level');
  const [wbsTree, setWbsTree] = useState(() => {
    try {
      const v = localStorage.getItem('gantt-wbs-tree');
      return v == null ? true : v === '1';
    } catch {
      return true;
    }
  });
  const [wbsLevelColors, setWbsLevelColors] = useState(loadWbsLevelColors);
  const [collapsedPaths, setCollapsedPaths] = useState(() => {
    try {
      const raw = localStorage.getItem(`gantt-wbs-collapsed:${jobNumber || ''}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const [colWidths, setColWidths] = useState(DATA_COLUMNS.reduce((a, c) => ({ ...a, [c.key]: c.width }), {}));
  const colResizeRef = useRef(null);
  const dragZoomRef = useRef(null);

  // Inline cell editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Fill-down state
  const [selectedCell, setSelectedCell] = useState(null);
  const [fillPreview, setFillPreview] = useState(null);
  const fillDragRef = useRef(null);
  const processedDataRef = useRef([]);

  // UDF (user-defined field) mappings
  const [udfMappings, setUdfMappings] = useState([]);
  const ganttOrderKeys = useMemo(() => {
    const base = layout === 'activity'
      ? ['notes_icon', 'wbs_name', ...activityLayoutColumns.map(c => c.key)]
      : DATA_COLUMNS.map(c => c.key);
    return [...base, ...udfMappings.map(m => m.column_key)];
  }, [layout, activityLayoutColumns, udfMappings]);
  const { columnOrder, moveColumn } = useColumnOrder(GANTT_PREFS.order, ganttOrderKeys);
  const [showUdfTab, setShowUdfTab] = useState(false);

  const colorMap = useMemo(() => {
    const map = {};
    const vals = [...new Set(ganttData.map(r => (r[colorByField] || '').toLowerCase().trim()).filter(Boolean))];
    vals.sort();
    vals.forEach((v, i) => {
      map[v] = PALETTE[v] || EXTRA_COLORS[i % EXTRA_COLORS.length];
    });
    return map;
  }, [ganttData, colorByField]);

  const getWbsLevelColor = useCallback((level) => {
    const i = Math.max(0, Number(level) || 0);
    return wbsLevelColors[String(i)] || DEFAULT_WBS_LEVEL_COLORS[i % DEFAULT_WBS_LEVEL_COLORS.length];
  }, [wbsLevelColors]);

  const getBarColor = useCallback((task) => {
    if (task._kind === 'wbs' || colorByField === 'wbs_level') {
      return getWbsLevelColor(wbsBandOf(task));
    }
    const val = (task[colorByField] || '').toLowerCase().trim();
    return colorMap[val] || '#6c757d';
  }, [colorByField, colorMap, getWbsLevelColor]);

  const setWbsLevelColor = useCallback((level, hex) => {
    setWbsLevelColors((prev) => {
      const next = { ...prev, [String(level)]: hex };
      try { localStorage.setItem(WBS_LEVEL_COLOR_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Dynamic filter columns based on current layout
  const filterColumns = useMemo(() => {
    if (layout === 'activity') {
      const cols = [
        { key: 'wbs_name', label: 'WBS Name', type: 'text' },
        { key: 'wbs_path', label: 'WBS Path', type: 'text' },
        ...activityLayoutColumns.map(c => ({
          key: c.key,
          label: c.label,
          type: c.key.includes('date') ? 'date' : (c.key === 'percent_complete' || c.key === 'duration' || c.key === 'remaining_duration' ? 'number' : 'text')
        }))
      ];
      return cols;
    }
    return DATA_COLUMNS.filter(c => c.filterable).map(c => ({ key: c.key, label: c.label, type: c.type || 'text' }));
  }, [layout, activityLayoutColumns]);

  const {
    filteredItems: filterResult, columnFilters, openFilterColumn, filterSearch, filterMode, filterText,
    filterButtonPositions, setOpenFilterColumn, setFilterSearch,
    getUniqueValues, handleFilterToggle, selectAllColumnFilter,
    clearColumnFilter, applyTextFilter, clearAllFilters, getActiveFilters,
    openFilterAtPosition, numericColumnKeys, dateColumnKeys, columnLabels,
  } = useFilterTemplate({ items: ganttData, columns: filterColumns });

  // Only fetch from /gantt endpoint as fallback when no data prop is provided
  const fetchGanttData = useCallback(async () => {
    if (!jobNumber || data.length > 0) return;
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/gantt`);
      if (response.data.success) {
        const d = (response.data.data || []).map(r => ({
          ...r,
          activity_id: r.activity_id || '',
          activity_name: r.activity_name || r.name || r.activity_id || '',
          start_date: r.start_date || r.start || '',
          finish_date: r.finish_date || r.end || '',
          wbs_path: r.wbs_path || '',
          wbs_name: r.wbs_name || '',
        })).filter(r => r.start_date && r.finish_date);
        setGanttData(d);
        if (d.length > 0) {
          const dates = d.flatMap(r => [new Date(r.start_date), new Date(r.finish_date)]);
          const mn = new Date(Math.min(...dates)); const mx = new Date(Math.max(...dates));
          mn.setDate(mn.getDate() - mn.getDay() - 7); mx.setDate(mx.getDate() + (6 - mx.getDay()) + 7);
          setDateRange({ start: mn, end: mx });
        }
      }
    } catch (err) { console.error('Error fetching gantt data:', err); setError('Failed to load Gantt chart data'); }
    finally { setLoading(false); }
  }, [instance, accounts, jobNumber, data.length]);

  useEffect(() => { if (data.length === 0) fetchGanttData(); }, [fetchGanttData, data.length]);

  useEffect(() => {
    if (data.length > 0) {
      const transformed = data.map(row => {
        const obj = { id: row.id?.toString() };
        ALL_DATA_KEYS.forEach(k => { obj[k] = row[k] ?? ''; });
        obj.wbs_path = row.wbs_path || obj.wbs_path || '';
        obj.activity_name = obj.activity_name || obj.activity_id;
        obj.notes = row.notes || '';
        obj.note_updated_by = row.note_updated_by_name || row.updated_by || '';
        obj.note_updated_at = row.updated_at || '';
        return obj;
      }).filter(d => d.start_date && d.finish_date);
      if (transformed.length > 0) {
        setGanttData(transformed);
        const dates = transformed.flatMap(d => [new Date(d.start_date), new Date(d.finish_date)]);
        const mn = new Date(Math.min(...dates)); const mx = new Date(Math.max(...dates));
        mn.setDate(mn.getDate() - mn.getDay() - 7); mx.setDate(mx.getDate() + (6 - mx.getDay()) + 7);
        setDateRange({ start: mn, end: mx }); setLoading(false);
      }
    }
  }, [data]);

  useEffect(() => {
    if (Object.keys(visibleDataColumns).length > 0) return;
    const saved = loadPrefJson(GANTT_PREFS.visible, null);
    if (saved && typeof saved === 'object' && Object.keys(saved).length) {
      const d = { ...saved };
      DATA_COLUMNS.forEach((c) => { if (d[c.key] === undefined && c.defaultHidden) d[c.key] = false; });
      activityLayoutColumns.forEach((c) => { if (d[c.key] === undefined && c.defaultHidden) d[c.key] = false; });
      setVisibleDataColumns(d);
      return;
    }
    const d = {};
    DATA_COLUMNS.forEach((c) => { d[c.key] = !c.defaultHidden; });
    activityLayoutColumns.forEach((c) => { d[c.key] = !c.defaultHidden; });
    d.notes_icon = true;
    setVisibleDataColumns(d);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!Object.keys(visibleDataColumns).length) return;
    try { localStorage.setItem(GANTT_PREFS.visible, JSON.stringify(visibleDataColumns)); } catch { /* ignore */ }
  }, [visibleDataColumns]);

  useEffect(() => { leftPanelWidthRef.current = leftPanelWidth; }, [leftPanelWidth]);
  useEffect(() => { zoomLevelRef.current = zoomLevel; }, [zoomLevel]);

  const commitZoom = useCallback((level) => {
    const z = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, level));
    setZoomLevel(z);
    zoomUserSetRef.current = true;
    try { localStorage.setItem(GANTT_PREFS.zoom, String(z)); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const applySplit = () => {
      if (isDragging) return;
      const r = containerRef.current?.getBoundingClientRect();
      if (!r || r.width < 400) return;
      const w = Math.max(200, Math.min(r.width * leftRatio, r.width - 200));
      setLeftPanelWidth(w);
    };
    applySplit();
    window.addEventListener('resize', applySplit);
    return () => window.removeEventListener('resize', applySplit);
  }, [leftRatio, isDragging, loading, ganttData.length]);

  useEffect(() => {
    if (zoomUserSetRef.current) return;
    if (!dateRange.start || !dateRange.end) return;
    const r = containerRef.current?.getBoundingClientRect();
    if (!r) return;
    const avail = r.width - leftPanelWidth - 6;
    if (avail < 80) return;
    setZoomLevel(fitZoomLevel(dateRange.start, dateRange.end, avail));
  }, [dateRange, leftPanelWidth, ganttData.length]);

  useEffect(() => {
    const move = (e) => {
      if (!isDragging) return;
      const r = containerRef.current?.getBoundingClientRect();
      if (r) setLeftPanelWidth(Math.max(200, Math.min(e.clientX - r.left, r.width - 200)));
    };
    const up = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const r = containerRef.current?.getBoundingClientRect();
      if (!r || r.width < 400) return;
      const ratio = Math.min(0.82, Math.max(0.28, leftPanelWidthRef.current / r.width));
      setLeftRatio(ratio);
      try { localStorage.setItem(GANTT_PREFS.leftRatio, String(ratio)); } catch { /* ignore */ }
    };
    if (isDragging) { document.addEventListener('mousemove', move); document.addEventListener('mouseup', up); document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; }
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [isDragging]);

  useEffect(() => {
    const ts = timescaleRef.current; if (!ts) return;
    const down = (e) => { if (e.target.closest('[data-no-drag-zoom]')) return; dragZoomRef.current = { startX: e.clientX, startZoom: zoomLevel }; e.preventDefault(); };
    const move = (e) => { if (!dragZoomRef.current) return; const nz = Math.max(0, Math.min(ZOOM_LEVELS.length - 1, dragZoomRef.current.startZoom + Math.round((e.clientX - dragZoomRef.current.startX) / 40))); if (nz !== zoomLevel) setZoomLevel(nz); };
    const up = () => {
      if (dragZoomRef.current) commitZoom(zoomLevelRef.current);
      dragZoomRef.current = null;
    };
    ts.addEventListener('mousedown', down); document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    return () => { ts.removeEventListener('mousedown', down); document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [zoomLevel, commitZoom]);

  const handleScroll = (src) => (e) => {
    if (src === 'left' && rightPanelRef.current) rightPanelRef.current.scrollTop = e.target.scrollTop;
    else if (src === 'right' && leftPanelRef.current) leftPanelRef.current.scrollTop = e.target.scrollTop;
  };

  const generateTimeUnits = useCallback(() => {
    if (!dateRange.start || !dateRange.end) return [];
    const zl = ZOOM_LEVELS[zoomLevel]; const units = []; const c = new Date(dateRange.start);
    if (zl.unit === 'week') { c.setDate(c.getDate() - c.getDay()); while (c <= dateRange.end) { units.push({ date: new Date(c), year: c.getFullYear(), month: c.toLocaleDateString('en-US', { month: 'short' }), monthNum: c.getMonth(), day: c.getDate() }); c.setDate(c.getDate() + 7); } }
    else if (zl.unit === 'month') { c.setDate(1); while (c <= dateRange.end) { units.push({ date: new Date(c), year: c.getFullYear(), month: c.toLocaleDateString('en-US', { month: 'short' }), monthNum: c.getMonth() }); c.setMonth(c.getMonth() + 1); } }
    else if (zl.unit === 'quarter') { c.setDate(1); c.setMonth(Math.floor(c.getMonth() / 3) * 3); while (c <= dateRange.end) { units.push({ date: new Date(c), year: c.getFullYear(), quarter: `Q${Math.floor(c.getMonth() / 3) + 1}`, monthNum: c.getMonth() }); c.setMonth(c.getMonth() + 3); } }
    else { c.setMonth(0); c.setDate(1); while (c <= dateRange.end) { units.push({ date: new Date(c), year: c.getFullYear() }); c.setFullYear(c.getFullYear() + 1); } }
    return units;
  }, [dateRange, zoomLevel]);

  const generateTimeHeaders = useCallback(() => {
    const units = generateTimeUnits(); if (units.length === 0) return { years: [], months: [], units: [] };
    const zl = ZOOM_LEVELS[zoomLevel]; const years = []; let cy = null, ys = 0;
    if (zl.unit === 'year') { units.forEach(u => years.push({ year: u.year, span: 1 })); return { years, months: [], units }; }
    const months = []; let cm = null, ms = 0;
    units.forEach((u, i) => { if (cy !== u.year) { if (cy !== null) years.push({ year: cy, span: ys }); cy = u.year; ys = 1; } else ys++; if (zl.unit === 'week') { const mk = `${u.year}-${u.monthNum}`; if (cm !== mk) { if (cm !== null) months.push({ month: units[i - 1].month, year: units[i - 1].year, span: ms }); cm = mk; ms = 1; } else ms++; } });
    if (cy !== null) years.push({ year: cy, span: ys }); if (zl.unit === 'week' && cm !== null) { const lu = units[units.length - 1]; months.push({ month: lu.month, year: lu.year, span: ms }); }
    return { years, months, units };
  }, [generateTimeUnits, zoomLevel]);

  const formatDate = (ds) => { if (!ds) return ''; return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };

  const openNotePopover = (task, event) => {
    event.stopPropagation();
    setActiveNotePopover(task.id);
    setNoteText(task.notes || '');
    setNoteHistory([]);
    setLoadingHistory(true);
    authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/activity/${task.id}/note-history`)
      .then(res => { if (res.data.success) setNoteHistory(res.data.data || []); })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  };
  const closeNotePopover = () => { setActiveNotePopover(null); setNoteText(''); setNoteHistory([]); };
  const saveNote = async () => {
    if (!activeNotePopover) return; setSavingNote(true);
    const now = new Date().toISOString();
    const userName = currentUserName || accounts?.[0]?.name || accounts?.[0]?.username || 'Unknown';
    const activityId = activeNotePopover;
    try {
      if (onUpdateActivityNote) await onUpdateActivityNote(activityId, noteText);
      else await authenticatedApiCall(instance, accounts, 'PUT', `/schedule-baseline/${jobNumber}/activity/${activityId}/notes`, { notes: noteText });
      setGanttData(prev => prev.map(t => t.id === activityId ? { ...t, notes: noteText, note_updated_by: userName, note_updated_at: now } : t));
      // Refresh history so user sees the saved note appear
      try {
        const histRes = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/activity/${activityId}/note-history`);
        if (histRes.data.success) setNoteHistory(histRes.data.data || []);
      } catch (_) {}
    } catch (err) { console.error('Error saving note:', err); } finally { setSavingNote(false); }
  };

  // Inline cell editing for Activity layout (%, remaining_duration, notes)
  const startCellEdit = (taskId, colKey, currentValue) => {
    if (readOnly || layout !== 'activity' || !EDITABLE_KEYS.has(colKey) || String(taskId).startsWith('wbs:')) return;
    setSelectedCell({ taskId, colKey });
    setEditingCell({ taskId, colKey });
    setEditingValue(currentValue == null ? '' : String(currentValue));
  };

  const saveCellEdit = async () => {
    if (!editingCell) return;
    const { taskId, colKey } = editingCell;
    const newVal = (colKey === 'notes' || colKey === 'comments') ? editingValue : (parseFloat(editingValue) || 0);
    const userName = currentUserName || accounts?.[0]?.name || accounts?.[0]?.username || 'Unknown';
    try {
      const payload = { [colKey]: newVal };
      if (onUpdateActivityProgress) {
        await onUpdateActivityProgress(taskId, payload);
      } else {
        await authenticatedApiCall(instance, accounts, 'PUT', `/schedule-baseline/${jobNumber}/activity/${taskId}/progress`, payload);
      }
      setGanttData(prev => prev.map(t => t.id === taskId ? { ...t, [colKey]: newVal, updated_by: userName, note_updated_by: userName, note_updated_at: new Date().toISOString() } : t));
    } catch (err) { console.error('Error saving cell:', err); }
    setEditingCell(null);
    setEditingValue('');
  };

  const cancelCellEdit = () => { setEditingCell(null); setEditingValue(''); };

  const handleFillDown = useCallback(async (sourceTaskId, colKey, targetTaskIds) => {
    if (!targetTaskIds.length) return;
    const sourceTask = ganttData.find(t => t.id === sourceTaskId);
    if (!sourceTask) return;
    const sourceVal = sourceTask[colKey];
    const isText = colKey === 'notes' || colKey === 'comments';
    const val = isText ? (sourceVal || '') : (parseFloat(sourceVal) || 0);

    setGanttData(prev => prev.map(t =>
      targetTaskIds.includes(t.id) ? { ...t, [colKey]: val } : t
    ));

    try {
      const updates = targetTaskIds.map(tid => ({ activityId: tid, [colKey]: val }));
      await authenticatedApiCall(instance, accounts, 'PUT',
        `/schedule-baseline/${jobNumber}/activity/batch-progress`,
        { updates }
      );
    } catch (err) {
      console.error('Fill-down batch save error:', err);
    }
  }, [ganttData, instance, accounts, jobNumber]);

  const startFillDrag = useCallback((e, taskId, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    fillDragRef.current = { sourceTaskId: taskId, colKey, startY: e.clientY, targetIds: [] };
    const onMove = (ev) => {
      const ref = fillDragRef.current;
      if (!ref) return;
      const deltaRows = Math.floor((ev.clientY - ref.startY) / rowHeight);
      const pd = processedDataRef.current;
      const srcIdx = pd.findIndex(t => t.id === ref.sourceTaskId);
      if (srcIdx < 0) return;
      const newTargets = deltaRows > 0
        ? pd.slice(srcIdx + 1, srcIdx + 1 + deltaRows).filter(t => t._kind !== 'wbs').map(t => t.id)
        : [];
      ref.targetIds = newTargets;
      setFillPreview(newTargets.length > 0 ? { colKey, targetIds: newTargets } : null);
    };
    const onUp = () => {
      const ref = fillDragRef.current;
      if (ref && ref.targetIds.length > 0) {
        handleFillDown(ref.sourceTaskId, ref.colKey, ref.targetIds);
      }
      fillDragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setFillPreview(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'crosshair';
    document.body.style.userSelect = 'none';
  }, [handleFillDown, rowHeight]);

  const handleSort = useCallback((ck) => { setSortConfig(prev => { if (prev.key !== ck) return { key: ck, direction: 'asc' }; if (prev.direction === 'asc') return { key: ck, direction: 'desc' }; return { key: null, direction: null }; }); }, []);

  const handleColResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colKey];
    colResizeRef.current = { colKey, startX, startW };
    const onMove = (ev) => {
      const ref = colResizeRef.current;
      if (!ref) return;
      setColWidths(prev => ({ ...prev, [ref.colKey]: Math.max(28, ref.startW + (ev.clientX - ref.startX)) }));
    };
    const onUp = () => { colResizeRef.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  }, [colWidths]);

  const autoFitColumns = useCallback(() => {
    const CELL_PAD = 24;
    const HEADER_PAD = 36;
    const MIN_W = 40;
    const MAX_W = 400;
    const HEADER_FONT = 'bold 11.5px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const CELL_FONT = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const allCols = layout === 'activity'
      ? [{ key: 'notes_icon', label: '', width: 28 }, { key: 'wbs_name', label: 'WBS Name', width: 140 }, ...activityLayoutColumns]
      : DATA_COLUMNS;
    const cols = allCols.filter(c => visibleDataColumns[c.key] !== false);
    const rows = ganttData;
    const sampled = rows.length > 200 ? rows.slice(0, 200) : rows;
    const newWidths = {};
    cols.forEach(col => {
      if (col.key === 'notes_icon') { newWidths[col.key] = 28; return; }
      let maxW = measureTextWidth(col.label, HEADER_FONT) + HEADER_PAD;
      for (const row of sampled) {
        const raw = row[col.key];
        const isDate = col.isDate || col.key.includes('date');
        const val = isDate ? formatDate(raw) : String(raw ?? '-');
        const treePad = wbsTree && (col.key === 'activity_name' || col.key === 'wbs_name') ? (WBS_INDENT * 3 + 28) : 0;
        const w = measureTextWidth(val, CELL_FONT) + CELL_PAD + treePad;
        if (w > maxW) maxW = w;
      }
      const fallback = AUTOFIT_FALLBACK_WIDTHS[col.key] || col.width || 80;
      newWidths[col.key] = Math.max(MIN_W, Math.min(MAX_W, Math.ceil(Math.max(maxW, fallback * 0.7))));
    });
    setColWidths(prev => ({ ...prev, ...newWidths }));
  }, [ganttData, visibleDataColumns, layout, activityLayoutColumns, wbsTree]);
  useEffect(() => {
    if (ganttData.length > 0 && !initialAutofitDone.current) {
      initialAutofitDone.current = true;
      autoFitColumns();
    }
  }, [ganttData, autoFitColumns]);
  const resetColumnWidths = useCallback(() => { setColWidths(DATA_COLUMNS.reduce((a, c) => ({ ...a, [c.key]: c.width }), {})); }, []);

  const hasUpdResp = useMemo(
    () => (data || []).some((row) => String(row.upd_resp || '').trim()),
    [data]
  );
  const lookAheadFrom = dataDate || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const lookWin = lookAheadOn ? lookAheadWindow(lookAheadFrom, 3) : null;

  useEffect(() => {
    if (!jobNumber || externalLoading) return;
    if (scopeJobRef.current === String(jobNumber)) return;
    scopeJobRef.current = String(jobNumber);
    let storedMine = null;
    let storedLook = null;
    try {
      storedMine = localStorage.getItem(`schedule-mine:${jobNumber}`);
      storedLook = localStorage.getItem(`schedule-lookahead:${jobNumber}`);
    } catch { /* ignore */ }
    const isUpdaterViewer = Boolean(viewerEmployeeCode || (viewerOrgTags && viewerOrgTags.length));
    if (storedMine === '0' || storedMine === '1') setMineOnly(storedMine === '1');
    else setMineOnly(Boolean(hasUpdResp && isUpdaterViewer));
    // Updaters land on their 3-week window by default; a stored choice wins.
    if (storedLook === '0' || storedLook === '1') setLookAheadOn(storedLook === '1');
    else setLookAheadOn(Boolean(hasUpdResp && isUpdaterViewer));
  }, [jobNumber, externalLoading, hasUpdResp, viewerEmployeeCode, viewerOrgTags]);

  const matchesScope = useCallback((row) => {
    if (mineOnly && !updRespMatchesViewer(row.upd_resp, { employeeCode: viewerEmployeeCode, orgTags: viewerOrgTags })) return false;
    if (lookAheadOn && !scheduleRowInLookAhead(row, lookAheadFrom, 3)) return false;
    return true;
  }, [mineOnly, lookAheadOn, viewerEmployeeCode, viewerOrgTags, lookAheadFrom]);

  const filteredChanges = useMemo(() => {
    const q = changeSearch.trim().toLowerCase();
    if (!q) return changes;
    const fieldLabel = { percent_complete: '%', remaining_duration: 'remaining duration', notes: 'notes', comments: 'comments' };
    return changes.filter((row) => {
      const field = fieldLabel[row.field_name] || row.field_name || '';
      const source = row.source === 'tracker-sync' ? 'tracker' : 'schedule';
      const blob = [row.activity_id, field, row.field_name, row.old_value, row.new_value, row.user_name, row.user_email, source].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [changes, changeSearch]);

  const loadChanges = useCallback(async () => {
    if (!jobNumber) return;
    setChangesLoading(true);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/audit`);
      setChanges(response.data?.data || []);
    } catch (err) {
      console.error('Error loading schedule changes:', err);
      setChanges([]);
    } finally {
      setChangesLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  const processedData = useMemo(() => {
    let result = [...filterResult].filter(matchesScope);
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(row => Object.values(row).some(val => String(val || '').toLowerCase().includes(s)));
    }
    if (wbsTree) {
      const outline = buildWbsOutline(result, { sortKey: sortConfig.key, sortDir: sortConfig.direction });
      return filterCollapsedRows(outline, collapsedPaths);
    }
    if (sortConfig.key) {
      result.sort((a, b) => {
        let av = a[sortConfig.key] || '', bv = b[sortConfig.key] || '';
        if (sortConfig.key === 'start_date' || sortConfig.key === 'finish_date') {
          av = new Date(av).getTime() || 0; bv = new Date(bv).getTime() || 0;
        }
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortConfig.direction === 'asc' ? av - bv : bv - av;
        }
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [filterResult, searchTerm, sortConfig, wbsTree, collapsedPaths, matchesScope]);
  processedDataRef.current = processedData;

  const wbsGroupSpans = useMemo(
    () => (wbsTree ? computeWbsGroupSpans(processedData) : []),
    [wbsTree, processedData]
  );

  const outlineWbsPaths = useMemo(() => {
    if (!wbsTree) return [];
    let result = [...filterResult].filter(matchesScope);
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(row => Object.values(row).some(val => String(val || '').toLowerCase().includes(s)));
    }
    return collectWbsPaths(buildWbsOutline(result, { sortKey: sortConfig.key, sortDir: sortConfig.direction }));
  }, [filterResult, searchTerm, sortConfig, wbsTree, matchesScope]);

  useEffect(() => {
    try { localStorage.setItem('gantt-wbs-tree', wbsTree ? '1' : '0'); } catch { /* ignore */ }
  }, [wbsTree]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(`gantt-wbs-collapsed:${jobNumber || ''}`);
      setCollapsedPaths(new Set(raw ? JSON.parse(raw) : []));
    } catch {
      setCollapsedPaths(new Set());
    }
  }, [jobNumber]);

  useEffect(() => {
    try {
      localStorage.setItem(`gantt-wbs-collapsed:${jobNumber || ''}`, JSON.stringify([...collapsedPaths]));
    } catch { /* ignore quota */ }
  }, [collapsedPaths]); // jobNumber is current when collapse state updates after a job switch

  const toggleWbs = useCallback((path) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  useEffect(() => {
    const fit = () => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const next = Math.max(360, Math.floor(window.innerHeight - top - 36));
      setChartHeight((prev) => (prev === next ? prev : next));
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro && containerRef.current) {
      const parent = containerRef.current.parentElement;
      if (parent) ro.observe(parent);
      if (parent?.parentElement) ro.observe(parent.parentElement);
    }
    window.addEventListener('resize', fit);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [externalLoading, ganttData.length, mineOnly, lookAheadOn]);

  if (loading || externalLoading) return <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}><p>Loading schedule data...</p></div>;
  if (error) return <div style={{ padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px' }}>{error}</div>;
  if (ganttData.length === 0) return <div style={{ padding: '3rem', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px dashed #dee2e6' }}><div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div><h3 style={{ margin: '0 0 0.5rem 0' }}>No Schedule Data</h3><p style={{ color: '#6c757d', margin: 0 }}>Import an Excel file to get started.</p></div>;

  const timeHeaders = generateTimeHeaders();
  const columnWidth = ZOOM_LEVELS[zoomLevel].columnWidth;
  const chartWidth = timeHeaders.units.length * columnWidth;
  const zl = ZOOM_LEVELS[zoomLevel];

  const baseColumns = layout === 'activity' 
    ? [
        { key: 'notes_icon', label: '', width: 28, filterable: false, sortable: false },
        { key: 'wbs_name', label: 'WBS Name', width: 180, filterable: true, sortable: true, type: 'text' },
        ...activityLayoutColumns.map(c => ({ ...c, width: c.width || 100, filterable: true, sortable: true, type: c.key.includes('date') ? 'date' : (c.key === 'percent_complete' || c.key === 'duration' || c.key === 'remaining_duration' ? 'number' : 'text'), isDate: c.key.includes('date') }))
      ]
    : DATA_COLUMNS;

  const dataColumns = [
    ...baseColumns,
    ...udfMappings.map(m => ({ key: m.column_key, label: m.custom_name || m.column_key.toUpperCase(), width: 100, filterable: true, sortable: true, type: 'text' }))
  ];

  const displayedDataColumns = applyColumnOrder(dataColumns, columnOrder).filter(c => visibleDataColumns[c.key] !== false);
  const totalDataWidth = displayedDataColumns.reduce((s, c) => s + (colWidths[c.key] || c.width), 0);
  // Rails get their own gutter at the far left so they never sit on top of the
  // first data column.
  const deepestRailLevel = wbsGroupSpans.length ? Math.max(...wbsGroupSpans.map((s) => s.level)) : -1;
  const railGutter = deepestRailLevel >= 0 ? WBS_RAIL_LEFT + (deepestRailLevel + 1) * WBS_RAIL_STEP : 0;
  const treeColumnKey = wbsTree
    ? (displayedDataColumns.some(c => c.key === 'activity_name')
      ? 'activity_name'
      : (displayedDataColumns.some(c => c.key === 'wbs_name') ? 'wbs_name' : null))
    : null;

  const getBarStyle = (task) => {
    if (!dateRange.start || !dateRange.end || !task.start_date || !task.finish_date) return { left: 0, width: 0 };
    const td = Math.ceil((dateRange.end - dateRange.start) / 864e5);
    const so = Math.ceil((new Date(task.start_date) - dateRange.start) / 864e5);
    const dur = Math.ceil((new Date(task.finish_date) - new Date(task.start_date)) / 864e5) + 1;
    return { left: (so / td) * chartWidth, width: Math.max((dur / td) * chartWidth, 4) };
  };

  const legendEntries = colorByField === 'wbs_level'
    ? []
    : Object.entries(colorMap).sort((a, b) => a[0].localeCompare(b[0]));
  const wbsLevelsPresent = wbsTree
    ? [...new Set(processedData.filter((r) => r._kind === 'wbs').map((r) => Number(r._level) || 0))].sort((a, b) => a - b)
    : [];
  const isEditable = (colKey) => !readOnly && layout === 'activity' && EDITABLE_KEYS.has(colKey);

  return (
    <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', backgroundColor: 'white' }} onClick={(e) => { if (!e.target.closest('[data-editable-cell]')) setSelectedCell(null); }}>
      {/* Toolbar */}
      <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa', flexWrap: 'wrap', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="🔍 Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '4px 28px 4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.78rem', width: '160px', maxWidth: '100%' }} />
            {searchTerm && <button onClick={() => setSearchTerm('')} style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#6c757d' }}>✕</button>}
          </div>
          {lastUpdated && <span style={{ fontSize: '0.72rem', color: '#6c757d', padding: '2px 6px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e9ecef' }}>Updated {new Date(lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
          {readOnly && <span style={{ fontSize: '0.72rem', fontWeight: 650, color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '9999px', padding: '2px 8px' }}>View only</span>}
          <button
            onClick={() => {
              const next = !mineOnly;
              setMineOnly(next);
              try { localStorage.setItem(`schedule-mine:${jobNumber || ''}`, next ? '1' : '0'); } catch { /* ignore */ }
            }}
            disabled={!(viewerEmployeeCode || (viewerOrgTags && viewerOrgTags.length))}
            style={mineOnly ? { ...BTN, backgroundColor: '#e0f2fe', borderColor: '#0284c7', color: '#075985' } : (!(viewerEmployeeCode || (viewerOrgTags && viewerOrgTags.length)) ? BTN_OFF : BTN)}
            title={viewerEmployeeCode
              ? `P6 Upd Resp ${viewerEmployeeCode}. On shows only your activities. Off shows every activity.`
              : (viewerOrgTags && viewerOrgTags.length
                ? `P6 Upd Resp ${viewerOrgTags[0]}. On shows only your company's activities. Off shows every activity.`
                : 'Your portal user is not linked to an employee code or a company, so this filter stays off.')}
          >
            {mineOnly ? `My activities · ${viewerEmployeeCode || (viewerOrgTags && viewerOrgTags[0]) || ''}` : 'All activities'}
          </button>
          <button
            onClick={() => {
              const next = !lookAheadOn;
              setLookAheadOn(next);
              try { localStorage.setItem(`schedule-lookahead:${jobNumber || ''}`, next ? '1' : '0'); } catch { /* ignore */ }
            }}
            style={lookAheadOn ? { ...BTN, backgroundColor: '#e0f2fe', borderColor: '#0284c7', color: '#075985' } : BTN}
            title={lookWin
              ? `Activities that overlap ${lookWin.start} to ${lookWin.end}, plus late remaining work. Complete work stays out. ${dataDate ? 'From the P6 data date.' : 'No P6 data date stored yet, so this uses today.'}`
              : 'Show the next 3 weeks from the P6 data date, plus late remaining work.'}
          >
            3-week look-ahead
          </button>
          <button
            onClick={() => { setShowChanges(true); loadChanges(); }}
            style={BTN}
            title="Who changed percent, remaining duration, notes, and comments"
          >
            Changes
          </button>
          {activeFilters.map(f => (
            <span key={f.key} style={{ padding: '2px 8px', backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '4px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {columnLabels[f.key] || f.key}: {f.type === 'values' ? `${f.count} values` : `${f.mode} "${f.value || ''}"`}
              <button onClick={() => clearColumnFilter(f.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', color: '#065f46', padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
          {sortConfig.key && <span style={{ padding: '2px 8px', backgroundColor: '#cce5ff', color: '#004085', borderRadius: '4px', fontSize: '0.7rem' }}>Sort: {dataColumns.find(c => c.key === sortConfig.key)?.label} {sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
          {hasActiveFilters && <button onClick={() => clearAllFilters()} style={{ ...BTN, fontSize: '0.7rem', padding: '2px 8px', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>Clear all</button>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginLeft: 'auto' }}>
          {wbsTree && (
            <>
              <button onClick={() => setCollapsedPaths(new Set())} style={BTN} title="Expand every branch">Expand</button>
              <button onClick={() => setCollapsedPaths(new Set(outlineWbsPaths))} style={BTN} title="Collapse every branch">Collapse</button>
            </>
          )}
          <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Zoom</span>
          <button onClick={() => commitZoom(zoomLevel - 1)} disabled={zoomLevel === 0} style={zoomLevel === 0 ? BTN_OFF : BTN}>−</button>
          <span style={{ fontSize: '0.72rem', minWidth: '62px', textAlign: 'center', color: '#495057' }}>{ZOOM_LEVELS[zoomLevel].label}</span>
          <button onClick={() => commitZoom(zoomLevel + 1)} disabled={zoomLevel === ZOOM_LEVELS.length - 1} style={zoomLevel === ZOOM_LEVELS.length - 1 ? BTN_OFF : BTN}>+</button>
          <button onClick={() => setShowHelpModal(true)} style={BTN}>Help</button>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <div style={{ position: 'relative' }}>
            <button
              ref={moreBtnRef}
              type="button"
              onClick={() => {
                if (showMore) { setShowMore(false); return; }
                const r = moreBtnRef.current?.getBoundingClientRect();
                if (r) setMoreBox({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
                setShowMore(true);
              }}
              style={showMore ? { ...BTN, backgroundColor: '#e0f2fe', borderColor: '#0284c7', color: '#075985' } : BTN}
            >
              More
            </button>
            {showMore && (
              <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: moreBox?.top ?? 0, right: moreBox?.right ?? 8, backgroundColor: 'white', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: 8, zIndex: 1002, width: 300, maxHeight: '70vh', overflowY: 'auto' }}>
                <div onClick={() => setShowMore(false)}>{fileMenu}</div>
                <div style={{ height: 1, background: '#e5e7eb', margin: '6px 4px' }} />
                <button type="button" onClick={() => setWbsTree((v) => !v)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderRadius: 4, background: wbsTree ? '#e0f2fe' : 'transparent', cursor: 'pointer', fontSize: '0.82rem' }}>{wbsTree ? 'WBS grouping on' : 'WBS grouping off'}</button>
                <button type="button" onClick={autoFitColumns} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer', fontSize: '0.82rem' }}>Auto-fit columns</button>
                <button type="button" onClick={resetColumnWidths} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer', fontSize: '0.82rem' }}>Reset column widths</button>
                <button type="button" onClick={() => setBarLabelMode((prev) => prev === 'inside' ? 'right' : 'inside')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer', fontSize: '0.82rem' }}>{barLabelMode === 'inside' ? 'Bar label: inside' : 'Bar label: to the right'}</button>
                <div style={{ padding: '7px 10px 4px' }}>
                  <div style={{ fontSize: '0.82rem', color: '#1f2937', marginBottom: 6 }}>Row height</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {Object.entries(ROW_SIZES).map(([id, spec]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setRowSize(id);
                          try { localStorage.setItem(ROW_SIZE_KEY, id); } catch { /* ignore */ }
                        }}
                        style={{
                          flex: 1,
                          padding: '5px 0',
                          borderRadius: 4,
                          border: `1px solid ${rowSize === id ? '#0284c7' : '#d1d5db'}`,
                          background: rowSize === id ? '#e0f2fe' : 'white',
                          color: rowSize === id ? '#075985' : '#374151',
                          cursor: 'pointer',
                          fontSize: '0.78rem',
                          fontWeight: rowSize === id ? 650 : 500,
                        }}
                      >
                        {spec.label}
                      </button>
                    ))}
                  </div>
                </div>
                <label style={{ display: 'block', padding: '7px 10px', fontSize: '0.82rem', color: '#374151' }}>
                  Colour bars
                  <select value={colorByField} onChange={(e) => setColorByField(e.target.value)} style={{ display: 'block', width: '100%', marginTop: 4, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.8rem' }}>
                    {COLOR_BY_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                </label>
                {colorByField === 'wbs_level' && wbsLevelsPresent.length > 0 && (
                  <div style={{ padding: '4px 10px 8px' }}>
                    {wbsLevelsPresent.map((lvl) => {
                      const color = getWbsLevelColor(lvl);
                      return (
                        <label key={lvl} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', marginBottom: 4, cursor: 'pointer' }}>
                          <input type="color" value={color} onChange={(e) => setWbsLevelColor(lvl, e.target.value)} style={{ width: 28, height: 18, padding: 0, border: 'none', background: 'transparent' }} />
                          Level {lvl + 1}
                        </label>
                      );
                    })}
                    <button type="button" onClick={() => { const next = defaultWbsLevelColorMap(); setWbsLevelColors(next); try { localStorage.setItem(WBS_LEVEL_COLOR_KEY, JSON.stringify(next)); } catch { /* ignore */ } }} style={{ ...BTN, marginTop: 4 }}>Reset colours</button>
                  </div>
                )}
                {colorByField !== 'wbs_level' && (
                  <div style={{ padding: '0 10px 8px', display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: '0.72rem' }}>
                    {legendEntries.length > 0 ? legendEntries.map(([name, color]) => (
                      <span key={name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, background: color, borderRadius: 2 }} />{name}</span>
                    )) : <span style={{ color: '#9ca3af' }}>No values</span>}
                  </div>
                )}
                <button type="button" onClick={() => setShowColumnConfig((v) => !v)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderRadius: 4, background: showColumnConfig ? '#e0f2fe' : 'transparent', cursor: 'pointer', fontSize: '0.82rem' }}>Columns</button>
                {showColumnConfig && (
                  <div style={{ padding: '4px 6px 8px' }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: '8px' }}>
                      <button onClick={() => setShowUdfTab(false)} style={{ flex: 1, padding: '6px', fontSize: '0.72rem', fontWeight: 600, border: 'none', borderBottom: !showUdfTab ? '2px solid #007bff' : '2px solid transparent', backgroundColor: 'transparent', cursor: 'pointer', color: !showUdfTab ? '#007bff' : '#666' }}>Visibility</button>
                      <button onClick={() => setShowUdfTab(true)} style={{ flex: 1, padding: '6px', fontSize: '0.72rem', fontWeight: 600, border: 'none', borderBottom: showUdfTab ? '2px solid #007bff' : '2px solid transparent', backgroundColor: 'transparent', cursor: 'pointer', color: showUdfTab ? '#007bff' : '#666' }}>UDF ({udfMappings.length})</button>
                    </div>
                    {!showUdfTab ? (
                      <ColumnConfigPanel
                        columns={applyColumnOrder(dataColumns.filter(c => c.key !== 'notes_icon'), columnOrder.filter(k => k !== 'notes_icon'))}
                        isVisible={(key) => visibleDataColumns[key] !== false}
                        onToggle={(key) => setVisibleDataColumns(prev => ({ ...prev, [key]: !(prev[key] !== false) }))}
                        onMove={(from, to) => {
                          const keys = applyColumnOrder(dataColumns.filter(c => c.key !== 'notes_icon'), columnOrder.filter(k => k !== 'notes_icon')).map(c => c.key);
                          const fromIdx = columnOrder.indexOf(keys[from]);
                          const toIdx = columnOrder.indexOf(keys[to]);
                          if (fromIdx >= 0 && toIdx >= 0) moveColumn(fromIdx, toIdx);
                        }}
                        onShowAll={() => { const v = {}; dataColumns.forEach(c => { v[c.key] = true; }); setVisibleDataColumns(v); }}
                        onHideAll={() => { const v = {}; dataColumns.forEach(c => { v[c.key] = c.key === 'notes_icon'; }); setVisibleDataColumns(v); }}
                        getLabel={(col) => `${col.label}${col.key.startsWith('udf') ? ' (UDF)' : ''}${isEditable(col.key) ? ' (editable)' : ''}`}
                      />
                    ) : (
                      <>
                        <p style={{ fontSize: '0.7rem', color: '#6c757d', margin: '0 0 8px' }}>Add up to 10 custom columns</p>
                        {udfMappings.map((m, i) => (
                          <div key={m.column_key} style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontSize: '0.65rem', color: '#9ca3af', width: '36px' }}>{m.column_key}</span>
                            <input type="text" placeholder="Column name" value={m.custom_name} onChange={e => { const u = [...udfMappings]; u[i] = { ...u[i], custom_name: e.target.value }; setUdfMappings(u); }} style={{ flex: 1, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '0.72rem' }} />
                            <button onClick={() => { const key = udfMappings[i].column_key; setUdfMappings(udfMappings.filter((_, j) => j !== i)); setVisibleDataColumns(prev => { const n = { ...prev }; delete n[key]; return n; }); }} style={{ padding: '2px 6px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.65rem' }}>✕</button>
                          </div>
                        ))}
                        {udfMappings.length < 10 && (
                          <button onClick={() => {
                            const used = new Set(udfMappings.map(m => m.column_key));
                            for (let i = 1; i <= 10; i++) { const k = `udf${String(i).padStart(2, '0')}`; if (!used.has(k)) { setUdfMappings([...udfMappings, { column_key: k, custom_name: '' }]); setVisibleDataColumns(prev => ({ ...prev, [k]: true })); break; } }
                          }} style={{ width: '100%', padding: '6px', fontSize: '0.72rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>+ Add UDF</button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {(mineOnly || lookAheadOn) && processedData.filter((r) => r._kind !== 'wbs').length === 0 && (
        <div style={{ padding: '1.25rem 1rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#334155', fontSize: '0.9rem' }}>
          <div style={{ fontWeight: 650, marginBottom: 4 }}>Nothing in this view</div>
          <div>
            {mineOnly && !hasUpdResp && 'This schedule has no P6 Upd Resp codes yet, so nothing is assigned to you. '}
            {mineOnly && hasUpdResp && `No activities list ${viewerEmployeeCode || (viewerOrgTags && viewerOrgTags[0]) || 'you'} as Upd Resp. `}
            {lookAheadOn && 'The 3-week look-ahead also hides complete work and anything outside the window, except late remaining work. '}
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            {mineOnly && (
              <button onClick={() => { setMineOnly(false); try { localStorage.setItem(`schedule-mine:${jobNumber || ''}`, '0'); } catch { /* ignore */ } }} style={BTN}>Show all activities</button>
            )}
            {lookAheadOn && (
              <button onClick={() => { setLookAheadOn(false); try { localStorage.setItem(`schedule-lookahead:${jobNumber || ''}`, '0'); } catch { /* ignore */ } }} style={BTN}>Show the full schedule</button>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div ref={containerRef} style={{ display: 'flex', height: chartHeight, position: 'relative' }}>
        {/* Left Panel */}
        <div ref={leftPanelRef} style={{ width: leftPanelWidth, minWidth: leftPanelWidth, maxWidth: leftPanelWidth, overflow: 'auto', backgroundColor: '#fff' }} onScroll={handleScroll('left')}>
          <div style={{ minWidth: totalDataWidth + railGutter }}>
            <div style={{ height: HEADER_HEIGHT, display: 'flex', backgroundColor: '#f1f3f5', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, zIndex: 5 }}>
              {railGutter > 0 && <div style={{ width: railGutter, minWidth: railGutter, flexShrink: 0 }} />}
              {displayedDataColumns.map((col, idx) => {
                const w = colWidths[col.key] || col.width;
                return (
                  <div key={col.key} style={{ width: w, minWidth: w, padding: '0 0.4rem', display: 'flex', alignItems: 'center', justifyContent: col.key === 'notes_icon' ? 'center' : 'flex-start', fontWeight: 600, fontSize: '0.72rem', borderRight: idx < displayedDataColumns.length - 1 ? '1px solid #dee2e6' : 'none', backgroundColor: isEditable(col.key) ? CREME_BG : (sortConfig.key === col.key ? '#e2e6ea' : '#f1f3f5'), gap: '3px', position: 'relative' }}>
                    {col.key === 'notes_icon' ? '📝' : (
                      <>
                        <span onClick={col.sortable ? () => handleSort(col.key) : undefined} style={{ cursor: col.sortable ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={col.label}>
                          {col.label}{sortConfig.key === col.key && <span style={{ fontSize: '0.65rem', marginLeft: '2px', color: '#007bff' }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                        </span>
                        {col.filterable && <span onClick={(e) => openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', opacity: (columnFilters[col.key]?.length > 0 || filterMode[col.key]) ? 1 : 0.4, flexShrink: 0, display: 'flex' }}><FilterIcon isActive={columnFilters[col.key]?.length > 0 || !!filterMode[col.key]} color="#666" /></span>}
                      </>
                    )}
                    {col.key !== 'notes_icon' && <div onMouseDown={(e) => handleColResizeStart(e, col.key)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px', cursor: 'col-resize', backgroundColor: 'transparent' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#007bff'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'} />}
                  </div>
                );
              })}
            </div>
            <div style={{ position: 'relative' }}>
            {processedData.map((task, index) => {
              const isWbs = task._kind === 'wbs';
              const wbsBand = wbsBandOf(task);
              const levelColor = getWbsLevelColor(wbsBand);
              const wbsText = isWbs ? readableTextOn(levelColor) : undefined;
              const rowBg = isWbs ? levelColor : (index % 2 === 0 ? '#ffffff' : '#f8f9fa');
              return (
              <div key={task.id} data-wbs-row={isWbs ? wbsBand : undefined} style={{ height: rowHeight, display: 'flex', borderBottom: isWbs ? '1px solid rgba(15,23,42,0.22)' : '1px solid #e9ecef', backgroundColor: rowBg, color: wbsText, position: 'relative', fontWeight: isWbs ? 650 : 400 }}>
                {railGutter > 0 && <div style={{ width: railGutter, minWidth: railGutter, flexShrink: 0 }} />}
                {displayedDataColumns.map((col, idx) => {
                  const w = colWidths[col.key] || col.width;
                  const editable = !isWbs && isEditable(col.key);
                  const isEditing = editingCell && editingCell.taskId === task.id && editingCell.colKey === col.key;
                  const isSelected = selectedCell && selectedCell.taskId === task.id && selectedCell.colKey === col.key && !isEditing;
                  const isInFillRange = fillPreview?.colKey === col.key && fillPreview?.targetIds?.includes(task.id);
                  const isTree = col.key === treeColumnKey;
                  const cellBg = isInFillRange ? '#cce5ff' : (editable ? CREME_BG : undefined);
                  const rawTitle = col.isDate ? formatDate(task[col.key]) : (isWbs && isTree ? task._path : (task[col.key] || ''));
                  return (
                    <div key={col.key} data-editable-cell={editable ? 'true' : undefined} style={{ width: w, minWidth: w, padding: col.key === 'notes_icon' ? '0' : '0 0.4rem', display: 'flex', alignItems: 'center', justifyContent: col.key === 'notes_icon' ? 'center' : 'flex-start', fontSize: rowSpec.font, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderRight: isWbs || idx >= displayedDataColumns.length - 1 ? 'none' : '1px solid #f0f0f0', backgroundColor: cellBg, cursor: isTree && isWbs ? 'pointer' : (editable ? 'pointer' : 'default'), position: 'relative', outline: isSelected ? '2px solid #007bff' : (isInFillRange ? '2px solid #80bdff' : 'none'), outlineOffset: '-2px', color: isWbs ? wbsText : undefined }} title={rawTitle} onClick={isTree && isWbs ? () => toggleWbs(task._path) : (editable && !isEditing ? () => startCellEdit(task.id, col.key, task[col.key]) : undefined)}>
                      {col.key === 'notes_icon' ? (
                        isWbs ? <span style={{ fontSize: '0.7rem', opacity: 0.3 }} title={task._path}>▣</span> : (
                        <button onClick={(e) => openNotePopover(task, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', fontSize: '0.85rem', opacity: task.notes ? 1 : 0.3 }} title={task.notes ? `${task.notes}\n— ${task.note_updated_by || '?'}, ${task.note_updated_at ? formatDate(task.note_updated_at) : '?'}` : 'Add note'}>📝</button>
                        )
                      ) : isEditing ? (
                        <input type={(col.key === 'notes' || col.key === 'comments') ? 'text' : 'number'} value={editingValue} onChange={e => setEditingValue(e.target.value)} onBlur={saveCellEdit} onKeyDown={e => { if (e.key === 'Enter') saveCellEdit(); if (e.key === 'Escape') cancelCellEdit(); }} autoFocus style={{ width: '100%', padding: '2px 4px', border: '1px solid #007bff', borderRadius: '3px', fontSize: rowSpec.font, outline: 'none', backgroundColor: '#fffef5' }} />
                      ) : isTree ? (
                        <span style={{ display: 'flex', alignItems: 'center', minWidth: 0, paddingLeft: (task._level || 0) * WBS_INDENT, width: '100%' }}>
                          {isWbs ? (
                            <span style={{ width: 14, flexShrink: 0, fontSize: '0.58rem', textAlign: 'center' }}>{collapsedPaths.has(task._path) ? '▶' : '▼'}</span>
                          ) : (
                            <span style={{ width: 14, flexShrink: 0 }} />
                          )}
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isWbs ? task._label : (task[col.key] || '-')}
                          </span>
                          {isWbs && (
                            <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 500, opacity: 0.75, flexShrink: 0 }}>{task._childActivityCount}</span>
                          )}
                        </span>
                      ) : col.isDate ? formatDate(task[col.key]) : (col.key === 'percent_complete' && task[col.key] != null && task[col.key] !== '' ? `${task[col.key]}%` : (task[col.key] || (isWbs ? '' : '-')))}
                      {isSelected && (
                        <div onMouseDown={(e) => startFillDrag(e, task.id, col.key)} style={{ position: 'absolute', right: -4, bottom: -4, width: 8, height: 8, backgroundColor: '#007bff', cursor: 'crosshair', zIndex: 5, border: '1px solid white' }} title="Drag to fill down" />
                      )}
                    </div>
                  );
                })}
                {activeNotePopover === task.id && (
                  <div style={{ position: 'absolute', top: 0, left: 35, zIndex: 1000, backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '0.75rem', width: '340px', maxHeight: '400px', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px', color: '#495057' }}>📝 Activity: {task.activity_id} — {task.activity_name}</div>
                    {task.note_updated_by && <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '6px' }}>Last edited by <strong>{task.note_updated_by}</strong> — {task.note_updated_at ? new Date(task.note_updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' }) : ''}</div>}
                    <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={readOnly ? 'No note' : 'Enter note...'} readOnly={readOnly} style={{ width: '100%', minHeight: '80px', padding: '0.5rem', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.8rem', resize: 'vertical', boxSizing: 'border-box', backgroundColor: readOnly ? '#f8f9fa' : 'white' }} autoFocus />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                      <button onClick={closeNotePopover} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', fontSize: '0.75rem', cursor: 'pointer' }}>{readOnly ? 'Close' : 'Cancel'}</button>
                      {!readOnly && <button onClick={saveNote} disabled={savingNote} style={{ padding: '0.35rem 0.75rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.75rem', cursor: savingNote ? 'wait' : 'pointer', opacity: savingNote ? 0.7 : 1 }}>{savingNote ? 'Saving...' : 'Save'}</button>}
                    </div>
                    <div style={{ marginTop: '8px', borderTop: '1px solid #e9ecef', paddingTop: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c757d', marginBottom: '4px' }}>Note History</div>
                      {loadingHistory ? <div style={{ fontSize: '0.7rem', color: '#adb5bd' }}>Loading...</div> : noteHistory.length === 0 ? <div style={{ fontSize: '0.7rem', color: '#adb5bd', fontStyle: 'italic' }}>No previous versions</div> : noteHistory.map((h, i) => (
                        <div key={i} style={{ padding: '4px 0', borderBottom: i < noteHistory.length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: '0.7rem' }}>
                          <div style={{ color: '#495057' }}>{h.note_text}</div>
                          <div style={{ color: '#adb5bd', fontSize: '0.65rem' }}>— {h.updated_by || '?'}, {h.updated_at ? new Date(h.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' }) : '?'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
            })}
            <WbsGroupRails spans={wbsGroupSpans} rowHeight={rowHeight} getColor={getWbsLevelColor} />
            </div>
          </div>
        </div>

        <div onMouseDown={() => setIsDragging(true)} style={{ width: '6px', backgroundColor: isDragging ? '#007bff' : '#dee2e6', cursor: 'col-resize', flexShrink: 0, transition: 'background-color 0.2s', zIndex: 20 }} onMouseEnter={(e) => e.target.style.backgroundColor = '#adb5bd'} onMouseLeave={(e) => { if (!isDragging) e.target.style.backgroundColor = '#dee2e6'; }} />

        {/* Right Panel */}
        <div ref={rightPanelRef} style={{ flex: 1, overflow: 'auto', backgroundColor: '#fff' }} onScroll={handleScroll('right')}>
          <div style={{ minWidth: chartWidth }}>
              <div ref={timescaleRef} style={{ height: HEADER_HEIGHT, position: 'sticky', top: 0, zIndex: 5, backgroundColor: '#f1f3f5', borderBottom: '2px solid #dee2e6', cursor: 'ew-resize', userSelect: 'none' }} title="Drag left/right to zoom (P6-style)">
                <div style={{ height: 24, display: 'flex', borderBottom: '1px solid #dee2e6' }}>
                  {timeHeaders.years.map((y, i) => <div key={`y-${i}`} style={{ width: y.span * columnWidth, minWidth: y.span * columnWidth, textAlign: 'center', fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e9ecef', borderRight: '1px solid #dee2e6' }}>{y.year}</div>)}
                </div>
                <div style={{ height: 24, display: 'flex', borderBottom: '1px solid #dee2e6' }}>
                  {zl.unit === 'week' ? (
                    timeHeaders.months.map((m, i) => <div key={`m-${i}`} style={{ width: m.span * columnWidth, minWidth: m.span * columnWidth, textAlign: 'center', fontWeight: 600, fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f3f5', borderRight: '1px solid #e9ecef' }}>{m.month}</div>)
                  ) : zl.unit !== 'year' ? (
                    timeHeaders.units.map((u, i) => <div key={`u2-${i}`} style={{ width: columnWidth, minWidth: columnWidth, textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f3f5', borderRight: '1px solid #e9ecef', color: '#495057' }}>{zl.unit === 'month' ? u.month : u.quarter}</div>)
                  ) : (
                    <div style={{ flex: 1, backgroundColor: '#f1f3f5' }} />
                  )}
                </div>
                <div style={{ height: 24, display: 'flex' }}>
                  {zl.unit === 'week' ? (
                    timeHeaders.units.map((u, i) => <div key={`u-${i}`} style={{ width: columnWidth, minWidth: columnWidth, textAlign: 'center', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', borderRight: '1px solid #f0f0f0', color: '#6c757d' }}>{u.month} {u.day}</div>)
                  ) : (
                    <div style={{ flex: 1, backgroundColor: '#f8f9fa' }} />
                  )}
                </div>
              </div>

              <div style={{ position: 'relative' }}>
              {processedData.map((task, index) => {
                const bs = getBarStyle(task);
                const isWbs = task._kind === 'wbs';
                const bc = getBarColor(task);
                const rowBg = isWbs ? '#f1f5f9' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa');
                const pct = Math.max(0, Math.min(100, parseFloat(task.percent_complete) || 0));
                return (
                  <div key={task.id} data-gantt-row={isWbs ? 'wbs' : 'activity'} style={{ height: rowHeight, position: 'relative', borderBottom: '1px solid #e9ecef', backgroundColor: rowBg }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', pointerEvents: 'none' }}>
                      {timeHeaders.units.map((_, i) => <div key={i} style={{ width: columnWidth, minWidth: columnWidth, borderRight: '1px solid #f5f5f5' }} />)}
                    </div>
                    {isWbs ? (
                      <div style={{ position: 'absolute', top: Math.round((rowHeight - Math.max(8, Math.round(rowHeight * 0.31))) / 2), left: bs.left, width: Math.max(bs.width, 8), height: Math.max(8, Math.round(rowHeight * 0.31)), backgroundColor: WBS_SUMMARY_BAR, clipPath: WBS_SUMMARY_CLIP, boxShadow: '0 1px 2px rgba(15,23,42,0.25)', overflow: 'hidden' }} title={`${task._path || task._label}\n${formatDate(task.start_date)} - ${formatDate(task.finish_date)}\n${task._childActivityCount} activities · ${pct}%`}>
                        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: mixHex(WBS_SUMMARY_BAR, '#ffffff', 0.35) }} />
                      </div>
                    ) : (
                      <div style={{ position: 'absolute', top: rowSpec.barPad, left: bs.left, width: bs.width, height: rowHeight - rowSpec.barPad * 2, backgroundColor: bc, borderRadius: '3px', display: 'flex', alignItems: 'center', paddingLeft: barLabelMode === 'inside' && bs.width > 30 ? '6px' : '0', color: 'white', fontSize: rowSpec.barFont, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.15)', cursor: 'pointer' }} title={`${task.activity_name}\n${formatDate(task.start_date)} - ${formatDate(task.finish_date)}\n${colorByField === 'wbs_level' ? `WBS Level ${wbsBandOf(task) + 1}` : `${COLOR_BY_FIELDS.find(f => f.key === colorByField)?.label}: ${task[colorByField] || 'N/A'}`}`}>
                        {barLabelMode === 'inside' && bs.width > 60 && task.activity_name}
                      </div>
                    )}
                    {barLabelMode === 'right' && !isWbs && <span style={{ position: 'absolute', top: Math.round((rowHeight - 14) / 2), left: bs.left + bs.width + 4, fontSize: rowSpec.barFont, color: '#495057', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{task.activity_name}</span>}
                    {barLabelMode === 'right' && isWbs && <span style={{ position: 'absolute', top: Math.round((rowHeight - 14) / 2), left: bs.left + Math.max(bs.width, 8) + 4, fontSize: rowSpec.barFont, color: WBS_SUMMARY_BAR, fontWeight: 650, whiteSpace: 'nowrap', pointerEvents: 'none' }}>{task._label}</span>}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
      </div>

      <div style={{ padding: '0.4rem 0.75rem', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa', fontSize: '0.7rem', color: '#6c757d' }}>
        Showing {processedData.filter((r) => r._kind !== 'wbs').length} of {ganttData.length} activities
        {lookWin ? ` · ${lookWin.start} to ${lookWin.end}${dataDate ? '' : ' (from today)'}` : ''}
        {wbsTree && <> · {processedData.filter((r) => r._kind === 'wbs').length} WBS</>}
        {dateRange.start && dateRange.end && <> | Range: {formatDate(dateRange.start)} - {formatDate(dateRange.end)}</>}
        {layout === 'activity' && ' | Creme columns (%, Rem. Duration, Notes) are editable — click to edit'}
      </div>

      {openFilterColumn && <FilterTemplateDropdown columnKey={openFilterColumn} columnLabel={columnLabels[openFilterColumn]} showTextFilters={true} isNumericColumn={numericColumnKeys.includes(openFilterColumn)} isDateColumn={dateColumnKeys.includes(openFilterColumn)} columnFilters={columnFilters} filterMode={filterMode} filterText={filterText} filterSearch={filterSearch} filterButtonPositions={filterButtonPositions} setOpenFilterColumn={setOpenFilterColumn} setFilterSearch={setFilterSearch} getUniqueValues={getUniqueValues} handleFilterToggle={handleFilterToggle} selectAllColumnFilter={selectAllColumnFilter} clearColumnFilter={clearColumnFilter} applyTextFilter={applyTextFilter} />}
      {activeNotePopover && <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={closeNotePopover} />}
      {(showMore || showColumnConfig) && <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} onClick={() => { setShowMore(false); setShowColumnConfig(false); }} />}

      {showHelpModal && (
        <div onClick={() => setShowHelpModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '12px', width: '900px', maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>❓ Schedule Help</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="schedule-gantt-help" title="Schedule Help" />
                <button onClick={() => setShowHelpModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', minHeight: 0 }}>
              <HelpWithVideoTour guideId="schedule-gantt-help" title="Schedule Help" accent="#0369a1">
              {/* Weekly loop chart — when trackers feed the schedule */}
              <div style={{ marginBottom: '1.5rem', border: '1px solid #c7d2fe', borderRadius: 10, background: 'linear-gradient(180deg,#eef2ff 0%,#f8fafc 100%)', padding: '1rem 1.1rem' }}>
                <div style={{ fontWeight: 700, color: '#312e81', fontSize: '0.95rem', marginBottom: 4 }}>Weekly schedule loop (Eastern Time)</div>
                <div style={{ fontSize: '0.78rem', color: '#4338ca', marginBottom: '0.85rem', lineHeight: 1.4 }}>
                  Tracker progress updates the schedule <strong>continuously</strong> from Monday 9&nbsp;PM until the next Monday 6&nbsp;AM export — not only at those two clock times.
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, alignItems: 'stretch' }}>
                  {/* Step 1 */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                    <div style={{ width: 88, flexShrink: 0, fontSize: '0.72rem', fontWeight: 700, color: '#1e3a8a', paddingTop: 10, lineHeight: 1.25 }}>Mon 9&nbsp;PM</div>
                    <div style={{ flex: 1, background: '#fff', border: '1px solid #93c5fd', borderRadius: 8, padding: '0.55rem 0.75rem' }}>
                      <div style={{ fontWeight: 650, color: '#1e40af', fontSize: '0.85rem' }}>1. P6 → Schedule Instances</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>Portal pulls the latest <em>submitted</em> P6 project and refreshes dates / structure / resources on this page.</div>
                    </div>
                  </div>
                  <div style={{ marginLeft: 100, width: 2, height: 14, background: '#94a3b8' }} />

                  {/* Live window — the missing piece */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                    <div style={{ width: 88, flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, color: '#065f46', paddingTop: 12, lineHeight: 1.25 }}>
                      Mon 9&nbsp;PM<br />→ next<br />Mon 6&nbsp;AM
                    </div>
                    <div style={{ flex: 1, background: '#ecfdf5', border: '2px solid #10b981', borderRadius: 8, padding: '0.65rem 0.75rem', boxShadow: '0 0 0 3px rgba(16,185,129,0.12)' }}>
                      <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.88rem' }}>2. LIVE — Trackers → schedule %</div>
                      <div style={{ fontSize: '0.75rem', color: '#047857', marginTop: 3, lineHeight: 1.4 }}>
                        All week long: when someone edits a progress tracker line that has an <strong>Activity ID</strong>, the portal rolls field completion (hours-weighted) into that activity&apos;s <strong>% complete</strong> on Schedule Instances — as edits happen.
                      </div>
                      <div style={{ marginTop: 8, fontSize: '0.72rem', color: '#334155', background: '#fff', borderRadius: 6, padding: '0.4rem 0.55rem', border: '1px dashed #86efac' }}>
                        <strong>Activity ID:</strong> the scheduler fills this on tracker lines for each job.
                        (One-time auto-backfill was used only for pilot job 61075 — not the ongoing process.)
                      </div>
                    </div>
                  </div>
                  <div style={{ marginLeft: 100, width: 2, height: 14, background: '#94a3b8' }} />

                  {/* Step 3 */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                    <div style={{ width: 88, flexShrink: 0, fontSize: '0.72rem', fontWeight: 700, color: '#9a3412', paddingTop: 10, lineHeight: 1.25 }}>Mon 6&nbsp;AM</div>
                    <div style={{ flex: 1, background: '#fff', border: '1px solid #fdba74', borderRadius: 8, padding: '0.55rem 0.75rem' }}>
                      <div style={{ fontWeight: 650, color: '#9a3412', fontSize: '0.85rem' }}>3. One Monday email → P6</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                        Excel for every job goes out in one Monday 6&nbsp;AM email to Yousef and Behzad, with a Portal import sheet of what changed since last week.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {[
                { icon: '🔍', title: 'Search', desc: 'Filters across all columns in real time.' },
                { icon: '🔽', title: 'Sorting', desc: 'Click column headers: ascending → descending → clear.' },
                { icon: '🔧', title: 'Filtering', desc: 'Funnel icon on every column header → Select Values or Advanced text/date filters.' },
                { icon: '↔️', title: 'Auto-fit', desc: 'Under More. Measures actual cell content and adjusts each column to fit the longest value.' },
                { icon: '🔄', title: 'Reset', desc: 'Under More. Restores all columns to their original default widths. You can also drag column borders manually.' },
                { icon: '🎨', title: 'Bar Colors', desc: 'Default is WBS Level — each outline depth has its own colour. Open More and pick a Level colour there (saved in this browser). Colour bars can switch to Discipline, Phase, Critical, and so on. WBS rows still keep their level colours.' },
                { icon: '📏', title: 'Bar Labels', desc: 'Under More. Inside the bar, or to the right of it.' },
                { icon: '↕️', title: 'Row height', desc: 'Under More. Small, Medium, or Large. Medium is the height the schedule opens with. This browser remembers the choice.' },
                { icon: '🔎', title: 'Zoom', desc: 'Starts fitted to the full project timeline. 10 levels (Year → Max Detail) via +/− or drag the timescale. Your zoom, splitter, and columns are remembered in this browser.' },
                { icon: '📝', title: 'Notes', desc: 'Two types: (1) Activity notes via the note icon — saved per activity with user name and timestamp; note history is shown in the popup. (2) Schedule notes under More — general project-level notes with author and timestamp. Both are included in Excel exports.' },
                { icon: '👁', title: 'Viewing', desc: 'Shows who currently has this schedule open. Green dot = your connection. Gray avatar = their tab is in the background. Two tabs of different pages keep you listed on both.' },
                { icon: '📅', title: '1 · Mon 9 PM — P6 pull', desc: 'Every Monday 9 PM Eastern the portal reads the latest submitted P6 schedule (estimate number + submit date) and refreshes dates/structure here. Estimate→job mapping comes from the admin panel.' },
                { icon: '🟢', title: '2 · LIVE Mon 9 PM → next Mon 6 AM — Trackers feed %', desc: 'Between the Monday night pull and the next Monday morning export, tracker edits roll into Schedule Instances.percent_complete: field % onto Activity ID (Pipe Install / Mechanical Install), shop % onto Fab Activity ID (Pipe Fab). Continuous — not a batch job at one clock time. Field Run shop lines are left out of fab %.' },
                { icon: '📤', title: '3 · Mon 6 AM — one email', desc: 'Every Monday 6 AM Eastern, one email to Yousef and Behzad lists every job. It attaches the full schedule workbooks and a Portal import workbook (Activity ID plus a note of what changed since last week). That note is also written into the P6 user field Portal import on each job’s Current project.' },
                { icon: '🔗', title: 'Activity ID ownership', desc: 'The scheduler puts Activity ID (install) and, on piping, Fab Activity ID on tracker lines so progress can feed the schedule. Auto-backfill was a one-time pilot for job 61075; it can also fill Fab Activity ID from Pipe Fab - {fluid} names.' },
                { icon: '📋', title: 'Update (legacy)', desc: 'Optional older workflow: set update day/time (CST) and responsible persons. A reminder still goes to those people at 7 AM CST, two days before. The old per-project Excel at 5 PM is retired; schedulers get the Monday email instead.' },
                { icon: '📐', title: 'Layout', desc: 'Under More. Activity layout (default): WBS, Activity ID/Name, Resp Party (P6 UDF), Resource Name (trade), Duration, Rem. Duration, %, Start, Finish. Resource Assignment layout: all resource-level columns. Both share the same data.' },
                { icon: '📊', title: 'WBS tree', desc: 'Default view. Activities sit under their Work Breakdown Structure and indent like Primavera. Each WBS colour frames its group — the same line colour and thickness on the top, left, and bottom — on both the table and the Gantt. Click the arrow to collapse a branch. Expand and Collapse are on the bar above the chart. WBS grouping can be turned off under More.' },
                { icon: '🚨', title: 'Critical', desc: 'Critical path column. Also available as a color-by option for the Gantt bars.' },
                { icon: '✏️', title: 'Inline Editing', desc: 'Creme columns (%, Remaining Duration, Notes, Comments) are editable for overrides. Prefer tracker-fed % via Activity ID. Fill-down works like Excel. Every change is kept in Changes: the old value, the new value, and who saved it. Tracker updates are logged as tracker-sync. Search in Changes finds an activity, a person, or a value.' },
                { icon: '👤', title: 'My activities', desc: 'Uses the P6 field Upd Resp, which is the employee code of the person who updates that activity. When the schedule has those codes and your portal user is linked to an employee code, the page opens on your activities. All activities shows the whole job. The choice is remembered in this browser.' },
                { icon: '✉️', title: 'Friday 7 AM — update request', desc: 'When the job\'s switch is on (Update → Friday 7 AM progress update request), each person on the list gets an e-mail from the portal, for Project Controls, every Friday at 7 AM Eastern. Who is on the list is up to the scheduler: the P6 Upd Resp field, a list typed in the Update window (one row per person with their slice — a WBS node, a resource, an activity prefix or the whole schedule), or both. Two PDFs are attached, filtered to that slice and laid out as the WBS tree like P6: the 3-week look-ahead (counted from the Sunday-midnight cut-off, the new data date) with write-in boxes, and the full schedule; the subject says how many activities fall in those three weeks. The project PM and Project Controls are in CC. It asks for physical % complete, remaining duration if known, obstacles, and date or sequence changes before the Sunday-midnight cut-off, returned by replying with the marked-up PDF or typing it in the e-mail. The e-mail does not point people here. Admins can see every job\'s switch under Admin → Notifications → Friday schedule updates.' },
                { icon: '📆', title: '3-week look-ahead', desc: 'Keeps activities whose start and finish overlap the P6 data date plus 21 days, plus late remaining work (finish already before the data date, or critical). Complete work stays out. If this job has not stored a P6 data date yet, the window starts today.' },
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <span style={{ fontSize: '1.3rem', width: '32px', textAlign: 'center' }}>{item.icon}</span>
                  <div><div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '2px' }}>{item.title}</div><div style={{ color: '#6b7280', fontSize: '0.85rem' }}>{item.desc}</div></div>
                </div>
              ))}
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {showChanges && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowChanges(false); setChangeSearch(''); }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 'min(920px, 94vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
              <div>
                <div style={{ fontWeight: 700 }}>Schedule changes</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Percent, remaining duration, notes, and comments. Newest first.</div>
              </div>
              <button onClick={() => { setShowChanges(false); setChangeSearch(''); }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ padding: '0.55rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
              <input
                type="text"
                value={changeSearch}
                onChange={(e) => setChangeSearch(e.target.value)}
                placeholder="Search activity, person, or value"
                autoFocus
                style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }}
              />
              {changeSearch.trim() && !changesLoading && changes.length > 0 && (
                <div style={{ marginTop: 4, fontSize: '0.72rem', color: '#6b7280' }}>{filteredChanges.length} of {changes.length}</div>
              )}
            </div>
            <div style={{ overflow: 'auto' }}>
              {changesLoading ? (
                <div style={{ padding: '1.5rem', color: '#6b7280' }}>Loading…</div>
              ) : changes.length === 0 ? (
                <div style={{ padding: '1.5rem', color: '#6b7280' }}>No progress or comment changes recorded yet.</div>
              ) : filteredChanges.length === 0 ? (
                <div style={{ padding: '1.5rem', color: '#6b7280' }}>No changes match that search.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                      {['When', 'Activity', 'Field', 'From', 'To', 'Who', 'Source'].map((h) => (
                        <th key={h} style={{ padding: '0.45rem 0.7rem', fontWeight: 650, borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#f8fafc' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChanges.map((row) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.4rem 0.7rem', whiteSpace: 'nowrap' }}>{row.created_at ? new Date(row.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Toronto' }) : ''}</td>
                        <td style={{ padding: '0.4rem 0.7rem' }}>{row.activity_id || ''}</td>
                        <td style={{ padding: '0.4rem 0.7rem' }}>{({ percent_complete: '%', remaining_duration: 'Remaining duration', notes: 'Notes', comments: 'Comments' })[row.field_name] || row.field_name}</td>
                        <td style={{ padding: '0.4rem 0.7rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.old_value || ''}>{row.old_value || '—'}</td>
                        <td style={{ padding: '0.4rem 0.7rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.new_value || ''}>{row.new_value || '—'}</td>
                        <td style={{ padding: '0.4rem 0.7rem' }}>{row.user_name || row.user_email || ''}</td>
                        <td style={{ padding: '0.4rem 0.7rem' }}>{row.source === 'tracker-sync' ? 'Tracker' : 'Schedule'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttChart;
